import { useTranslation } from 'react-i18next';
import { CatalogueDocument, sortSections } from '@quizdb/common';

export interface MaterialNameFieldsProps {
  document: string;
  onDocumentChange(value: string): void;
  section: string;
  onSectionChange(value: string): void;
  /** Every catalogued document, whatever its counts (QZ-D103), in the order held. */
  documents: CatalogueDocument[];
  documentError?: string;
  sectionError?: string;
  disabled?: boolean;
}

/**
 * New Job's document and optional section, shared by the PDF and photo methods (QZ-D100,
 * QZ-D101): one pair of text inputs, each suggesting from the catalogue through a native
 * `<datalist>` (QZ-D107). The section's suggestions are the sections of the document whose name
 * matches `document.trim()` exactly, ordered by `sortSections`, which goes through
 * `compareSectionNames` (QZ-D101); no match, or an empty box, gives an empty datalist. Controlled
 * and stateless: typing calls the change handler with the raw value, trimming being the page's.
 */
export const MaterialNameFields = ({
  document,
  onDocumentChange,
  section,
  onSectionChange,
  documents,
  documentError,
  sectionError,
  disabled,
}: MaterialNameFieldsProps) => {
  const { t } = useTranslation();

  const matched = documents.find((catalogued) => catalogued.document === document.trim());
  const sections = matched ? sortSections(matched.sections) : [];

  return (
    <div className="row g-3">
      <div className="col-sm-6">
        <label htmlFor="job-document" className="form-label">
          {t('job.new.document')}
        </label>
        <input
          id="job-document"
          type="text"
          maxLength={200}
          list="job-document-options"
          className={`form-control${documentError ? ' is-invalid' : ''}`}
          aria-describedby={`job-document-help${documentError ? ' job-document-error' : ''}`}
          disabled={disabled}
          value={document}
          onChange={(e) => onDocumentChange(e.target.value)}
        />
        <datalist id="job-document-options">
          {documents.map((catalogued) => (
            <option key={catalogued.document} value={catalogued.document} />
          ))}
        </datalist>
        <div id="job-document-help" className="form-text">
          {t('job.new.documentHelp')}
        </div>
        {documentError && (
          <div id="job-document-error" className="invalid-feedback d-block">
            {documentError}
          </div>
        )}
      </div>

      <div className="col-sm-6">
        <label htmlFor="job-section" className="form-label">
          {t('job.new.section')}
        </label>
        <input
          id="job-section"
          type="text"
          maxLength={200}
          list="job-section-options"
          className={`form-control${sectionError ? ' is-invalid' : ''}`}
          aria-describedby={`job-section-help${sectionError ? ' job-section-error' : ''}`}
          disabled={disabled}
          value={section}
          onChange={(e) => onSectionChange(e.target.value)}
        />
        <datalist id="job-section-options">
          {sections.map((catalogued) => (
            <option key={catalogued.name} value={catalogued.name} />
          ))}
        </datalist>
        <div id="job-section-help" className="form-text">
          {t('job.new.sectionHelp')}
        </div>
        {sectionError && (
          <div id="job-section-error" className="invalid-feedback d-block">
            {sectionError}
          </div>
        )}
      </div>
    </div>
  );
};
